/* TK product cards (snippets/grid-view-item.liquid, snippets/tk-variant-links.liquid, snippets/tk-card-video.liquid)
   Loaded once per page from layout/theme.liquid, so cards added later (infinite scroll, recommendations fetched
   by script) work too.
   1. "+" size picker: one delegated listener, so cards added by infinite scroll work without re-binding.
   2. In-view card videos: a <video> exists only while its card is on screen, and at most MAX_ACTIVE at once.
      Leaving the screen pauses it, drops its source and removes it from the page, so memory stays flat
      however far infinite scroll goes.
   3. Photo gallery on every card (data-tk-gallery = the product's other photos, space separated): dots, swipe on
      touch screens, and on desktop the photo follows the mouse across the card. Nothing is built or downloaded until
      the card is near the screen (and photos only when the shopper swipes or hovers); a card that scrolls far away
      goes back to a single photo, so memory stays flat with any number of products. */
if (!window.__tkCard) {
  window.__tkCard = true;

  /* ---------- Size picker ---------- */
  let openQuick = null;

  const setOpen = (quick, open) => {
    const btn = quick.querySelector('.tk-quick__toggle');
    quick.classList.toggle('is-open', open);
    if (btn) {
      btn.setAttribute('aria-expanded', String(open));
      const label = open ? btn.dataset.labelClose : btn.dataset.labelOpen;
      if (label) btn.setAttribute('aria-label', label);
    }
    if (open) openQuick = quick;
    else if (openQuick === quick) openQuick = null;
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.tk-quick__toggle');
    if (btn) {
      event.preventDefault();
      event.stopPropagation();
      const quick = btn.closest('.tk-quick');
      const willOpen = !quick.classList.contains('is-open');
      if (openQuick && openQuick !== quick) setOpen(openQuick, false);
      setOpen(quick, willOpen);
      return;
    }
    if (openQuick && !openQuick.contains(event.target)) setOpen(openQuick, false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openQuick) return;
    const btn = openQuick.querySelector('.tk-quick__toggle');
    setOpen(openQuick, false);
    if (btn) btn.focus();
  });

  /* ---------- In-view videos ---------- */
  const MAX_ACTIVE = 4;
  const active = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) el.start();
      else if (entry.intersectionRatio < 0.1) el.stop();
    }
  }, { threshold: [0, 0.1, 0.5] });

  class TkCardVideo extends HTMLElement {
    connectedCallback() {
      if (reducedMotion.matches || saveData) return;
      observer.observe(this);
    }

    disconnectedCallback() {
      observer.unobserve(this);
      this.stop();
    }

    pickSource(video) {
      // Native HLS (Safari / iOS) starts small and adapts; everything else gets the mp4.
      if (this.dataset.hls && video.canPlayType('application/vnd.apple.mpegurl')) return this.dataset.hls;
      return this.dataset.src || '';
    }

    start() {
      if (document.hidden) return;
      if (this.video) {
        this.video.play().catch(() => {});
        return;
      }
      if (active.size >= MAX_ACTIVE) active.values().next().value.stop(); // oldest one makes room

      const video = document.createElement('video');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.autoplay = true;
      video.preload = 'auto';
      video.disablePictureInPicture = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      video.addEventListener('error', () => this.stop(), { once: true });
      const src = this.pickSource(video);
      if (!src) return;
      video.src = src;

      this.video = video;
      active.add(this);
      this.appendChild(video);
      video.play().catch(() => {});
    }

    stop() {
      const video = this.video;
      if (!video) return;
      this.video = null;
      active.delete(this);
      this.classList.remove('is-playing');
      video.pause();
      video.removeAttribute('src');
      video.load(); // releases the decoder and buffered frames
      video.remove();
    }
  }

  if (!customElements.get('tk-card-video')) customElements.define('tk-card-video', TkCardVideo);

  // Background tab: free everything; the observer restarts what is on screen when the tab comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      [...active].forEach((el) => el.stop());
    } else {
      document.querySelectorAll('tk-card-video').forEach((el) => {
        observer.unobserve(el);
        if (!reducedMotion.matches && !saveData) observer.observe(el);
      });
    }
  });

  /* ---------- Photo gallery ---------- */
  const GAL_MAX = 8;
  const touchUI = window.matchMedia('(hover: none), (pointer: coarse)');
  const WIDTHS = [360, 540, 720, 900, 1080];

  const css = `
[data-tk-gal-watch]{position:relative}
.tk-gal{position:absolute;inset:0;z-index:2;pointer-events:none}
.tk-gal__track{position:absolute;inset:0;display:flex;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain;pointer-events:auto;touch-action:pan-x pan-y}
.tk-gal__track::-webkit-scrollbar{display:none}
.tk-gal__slide{flex:0 0 100%;height:100%;scroll-snap-align:start;scroll-snap-stop:always;background:transparent}
.tk-gal__slide+.tk-gal__slide{background:#f2f2f2}
.tk-gal__slide img,.tk-gal__img{display:block;width:100%;height:100%;object-fit:cover;object-position:center}
.tk-gal__img{position:absolute;inset:0;opacity:0;background:#f2f2f2}
.tk-gal__img.is-on{opacity:1}
.tk-gal__dots{position:absolute;left:0;right:0;bottom:10px;display:flex;justify-content:center;gap:5px;pointer-events:none;transition:opacity .2s ease}
.tk-gal__dot{width:5px;height:5px;border-radius:50%;background:rgba(0,0,0,.22);box-shadow:0 0 0 1px rgba(255,255,255,.55)}
.tk-gal__dot.is-active{background:#000}
@media (hover:hover) and (pointer:fine){.tk-gal__dots{opacity:0}.tk-gal.is-hover .tk-gal__dots{opacity:1}}
[data-tk-cols="8"] .tk-gal__dots{display:none}
@media (max-width:989px){[data-tk-cols-m="4"] .tk-gal__dots{display:none}}
`;
  const style = document.createElement('style');
  style.setAttribute('data-tk-gal', '');
  style.textContent = css;
  document.head.appendChild(style);

  const sized = (url, el) => {
    const w = (el.clientWidth || 300) * Math.min(window.devicePixelRatio || 1, 2);
    const pick = WIDTHS.find((x) => x >= w) || WIDTHS[WIDTHS.length - 1];
    return url.replace(/([?&])width=\d+/, '$1width=' + pick);
  };

  const urlsOf = (link) => (link.getAttribute('data-tk-gallery') || '').split(/\s+/).filter(Boolean).slice(0, GAL_MAX - 1);

  const setActive = (gal, i) => {
    gal._i = i;
    gal.querySelectorAll('.tk-gal__dot').forEach((d, di) => d.classList.toggle('is-active', di === i));
  };

  const build = (link) => {
    if (link._gal) return;
    const urls = urlsOf(link);
    if (!urls.length) return;
    const gal = document.createElement('div');
    gal.className = 'tk-gal';
    gal.setAttribute('aria-hidden', 'true');
    gal._urls = urls;
    gal._i = 0;
    const n = urls.length + 1;
    const dots = document.createElement('div');
    dots.className = 'tk-gal__dots';
    for (let i = 0; i < n; i++) {
      const d = document.createElement('span');
      d.className = 'tk-gal__dot' + (i === 0 ? ' is-active' : '');
      dots.appendChild(d);
    }

    if (touchUI.matches) {
      // Phones/tablets: a swipeable strip over the card photo. Slide 1 is see-through (the card's own photo or
      // video shows); the others get their photo when the shopper swipes towards them.
      const track = document.createElement('div');
      track.className = 'tk-gal__track';
      for (let i = 0; i < n; i++) {
        const slide = document.createElement('div');
        slide.className = 'tk-gal__slide';
        track.appendChild(slide);
      }
      const load = (i) => {
        if (i < 1 || i >= n) return;
        const slide = track.children[i];
        if (slide.firstChild) return;
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.src = sized(urls[i - 1], link);
        slide.appendChild(img);
      };
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
          if (i !== gal._i) setActive(gal, i);
          load(i);
          load(i + 1);
        });
      }, { passive: true });
      // A finger on the card is the first sign of a swipe: fetch the next photo now so it's there when it slides in.
      track.addEventListener('touchstart', () => load(gal._i + 1), { passive: true });
      gal.appendChild(track);
    } else {
      // Desktop: one photo layer that follows the mouse across the card (left edge = first photo).
      const img = document.createElement('img');
      img.className = 'tk-gal__img';
      img.alt = '';
      img.decoding = 'async';
      gal.appendChild(img);
      gal._img = img;
    }
    gal.appendChild(dots);
    link.appendChild(gal);
    link._gal = gal;
  };

  const destroy = (link) => {
    const gal = link._gal;
    if (!gal) return;
    link._gal = null;
    gal.querySelectorAll('img').forEach((img) => { img.removeAttribute('src'); });
    gal.remove();
  };

  // Desktop hover: delegated, so it works for any card on the page.
  const preload = (gal, i, link) => {
    if (i < 1 || i > gal._urls.length) return null;
    gal._cache = gal._cache || {};
    if (!gal._cache[i]) {
      const im = new Image();
      im.decoding = 'async';
      im.src = sized(gal._urls[i - 1], link);
      gal._cache[i] = im;
    }
    return gal._cache[i];
  };
  const showDesk = (gal, i, link) => {
    if (i === gal._i) return;
    setActive(gal, i);
    const img = gal._img;
    if (!img) return;
    if (i === 0) { img.classList.remove('is-on'); return; }
    const im = preload(gal, i, link);
    img.src = im.src;
    img.classList.add('is-on');
    preload(gal, i + 1, link);
  };
  let hoverLink = null;
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const link = event.target.closest && event.target.closest('[data-tk-gallery]');
    if (link === hoverLink) return;
    if (hoverLink && hoverLink._gal) { hoverLink._gal.classList.remove('is-hover'); showDesk(hoverLink._gal, 0, hoverLink); }
    hoverLink = link;
    if (!link) return;
    build(link);
    const gal = link._gal;
    if (!gal || !gal._img) return;
    gal.classList.add('is-hover');
    preload(gal, 1, link);
  });
  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse' || !hoverLink || !hoverLink._gal || !hoverLink._gal._img) return;
    const r = hoverLink.getBoundingClientRect();
    const n = hoverLink._gal._urls.length + 1;
    const i = Math.max(0, Math.min(n - 1, Math.floor(((event.clientX - r.left) / (r.width || 1)) * n)));
    showDesk(hoverLink._gal, i, hoverLink);
  }, { passive: true });

  // Build near the screen, drop when far away.
  const galObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) { if (touchUI.matches) build(entry.target); }
      else destroy(entry.target);
    }
  }, { rootMargin: '150% 0px 150% 0px' });

  const watchGalleries = (root) => {
    (root || document).querySelectorAll('[data-tk-gallery]:not([data-tk-gal-watch])').forEach((link) => {
      link.setAttribute('data-tk-gal-watch', '');
      galObserver.observe(link);
    });
  };
  const unwatch = (node) => {
    const links = node.matches && node.matches('[data-tk-gal-watch]') ? [node] : node.querySelectorAll ? node.querySelectorAll('[data-tk-gal-watch]') : [];
    links.forEach((link) => { galObserver.unobserve(link); destroy(link); });
  };

  let scanQueued = false;
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => { scanQueued = false; watchGalleries(); });
  };
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) if (node.nodeType === 1) { queueScan(); break; }
      for (const node of m.removedNodes) if (node.nodeType === 1) unwatch(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => watchGalleries());
  else watchGalleries();
  // Switching between touch and mouse (tablet with a mouse, devtools): rebuild in the right mode.
  touchUI.addEventListener && touchUI.addEventListener('change', () => {
    document.querySelectorAll('[data-tk-gal-watch]').forEach((link) => destroy(link));
  });
}
